import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import EmailComposerModal from './EmailComposerModal';
import EmailBodyEditor from './EmailBodyEditor';
import { getDisplayName } from '../utils/flyerDisplay';
import { TEMPLATE_FIELDS } from '../constants/templateFields';
import { validateMergeAgainstAllowlist } from '../utils/emailMergeTokens';
import { htmlToReadableText, unifyTemplateForAdminDraft } from '../utils/emailHtmlUtils';
import './EmailTemplatesAdmin.css';

const TEMPLATES_COLLECTION = 'emailTemplates';
const SENDS_COLLECTION = 'emailSends';

/** Keys that match EmailComposerModal template dropdown (for test-send mode). */
const COMPOSER_TEMPLATE_KEYS = new Set([
  'mentor_warning',
  'team_intro',
  'after_party_desc_budget',
  'mentor_load_summary',
  'early_load_warning',
  'recurrency_update',
  'recurrency_class_reminder',
  'fury_lab_initial',
  'fury_lab_reminder',
]);

function normalizeTemplateRecord(id, data) {
  const d = data || {};
  return {
    id,
    key: d.key || id,
    label: d.label || id,
    allowedContexts: Array.isArray(d.allowedContexts) ? d.allowedContexts : [],
    allowedMergeFields: Array.isArray(d.allowedMergeFields) ? d.allowedMergeFields : [],
    category: typeof d.category === 'string' ? d.category : '',
    isActive: d.isActive !== false,
    format: d.format || 'html',
    subjectTemplate: d.subjectTemplate || '',
    bodyHtmlTemplate: d.bodyHtmlTemplate || '',
    bodyTextTemplate: d.bodyTextTemplate || '',
    updatedAt: d.updatedAt || null,
    updatedByUid: d.updatedByUid || null,
  };
}

function normalizeTemplate(docSnap) {
  return normalizeTemplateRecord(docSnap.id, docSnap.data() || {});
}

const EmailTemplatesAdmin = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyRecipients, setHistoryRecipients] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [testTo, setTestTo] = useState('');
  const [showComposer, setShowComposer] = useState(false);
  const [testContextExtra, setTestContextExtra] = useState(null);
  const [activeTab, setActiveTab] = useState('edit'); // edit | history | fields | allEmails
  const [fieldsScope, setFieldsScope] = useState('all'); // all | mailMerge | legacyHtmlToken | furyLab
  const [fieldsSearch, setFieldsSearch] = useState('');
  const [allEmails, setAllEmails] = useState([]);
  const [loadingAllEmails, setLoadingAllEmails] = useState(false);
  const [mergeFieldInput, setMergeFieldInput] = useState('');

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId]
  );

  useEffect(() => {
    let cancelled = false;
    async function loadTemplates() {
      try {
        setLoadingTemplates(true);
        const snap = await getDocs(collection(firestore, TEMPLATES_COLLECTION));
        if (cancelled) return;
        const list = snap.docs.map(normalizeTemplate).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );
        setTemplates(list);
        if (list.length && !selectedId && !isCreatingNew) {
          setSelectedId(list[0].id);
          setDraft(unifyTemplateForAdminDraft(list[0]));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load email templates:', err);
        if (!cancelled) {
          setStatusMessage(err.message || 'Failed to load templates.');
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    }
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [selectedId, isCreatingNew]);

  useEffect(() => {
    const email = auth.currentUser?.email;
    if (email) {
      setTestTo((prev) => (prev && prev.trim() ? prev : email));
    }
  }, []);

  useEffect(() => {
    if (!selectedTemplate) {
      if (isCreatingNew) {
        setHistory([]);
        setHistoryRecipients({});
        return;
      }
      setDraft(null);
      setHistory([]);
      setHistoryRecipients({});
      return;
    }
    setDraft(unifyTemplateForAdminDraft(selectedTemplate));
    let cancelled = false;
    async function loadHistory() {
      try {
        setLoadingHistory(true);
        const q = query(
          collection(firestore, SENDS_COLLECTION),
          where('templateKey', '==', selectedTemplate.key),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setHistory(rows);

        // Enrich with recipient names for rows that have an eventId.
        const eventIds = Array.from(
          new Set(rows.map((r) => r.eventId).filter(Boolean))
        );
        const flyersByEvent = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const eventId of eventIds) {
          const fSnap = await getDocs(
            query(
              collection(firestore, 'flyers'),
              where('eventId', '==', eventId)
            )
          );
          const map = {};
          fSnap.docs.forEach((d) => {
            const data = { id: d.id, ...d.data() };
            const email = (data.email || '').toString().trim().toLowerCase();
            if (email) {
              map[email] = data;
            }
          });
          flyersByEvent[eventId] = map;
        }

        const recMap = {};
        rows.forEach((row) => {
          const emails = []
            .concat(
              (row.finalTo || '')
                .split(',')
                .map((e) => e.trim())
                .filter(Boolean)
            )
            .concat(
              (row.finalBcc || '')
                .split(',')
                .map((e) => e.trim())
                .filter(Boolean)
            );
          const seen = new Set();
          const list = [];
          emails.forEach((raw) => {
            const email = raw.toLowerCase();
            if (seen.has(email)) return;
            seen.add(email);
            const flyerMap = row.eventId ? flyersByEvent[row.eventId] || {} : {};
            const flyer = flyerMap[email] || null;
            const name = flyer ? getDisplayName(flyer) || flyer.email || raw : raw;
            list.push({ email: raw, name });
          });
          recMap[row.id] = list;
        });
        setHistoryRecipients(recMap);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load email send history:', err);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    }
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [selectedTemplate, isCreatingNew]);

  useEffect(() => {
    if (activeTab !== 'allEmails') return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingAllEmails(true);
        const q = query(
          collection(firestore, SENDS_COLLECTION),
          orderBy('createdAt', 'desc'),
          limit(200)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        setAllEmails(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load all email sends:', err);
        if (!cancelled) setAllEmails([]);
      } finally {
        if (!cancelled) setLoadingAllEmails(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const handleSelect = async (id) => {
    setIsCreatingNew(false);
    setSelectedId(id);
    const tmpl = templates.find((t) => t.id === id);
    if (tmpl) {
      setDraft(unifyTemplateForAdminDraft(tmpl));
    } else {
      setDraft(null);
    }
  };

  const handleFieldChange = (field, value) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            [field]: value,
          }
        : prev
    );
  };

  const handleSave = async () => {
    if (!draft) return;
    setIsCreatingNew(false);
    const id = draft.id || draft.key;
    if (!id) {
      setStatusMessage('Template key is required.');
      return;
    }
    const allow = Array.isArray(draft.allowedMergeFields) ? draft.allowedMergeFields : [];
    const bodyForValidate = draft.bodyHtmlTemplate || '';
    const check = validateMergeAgainstAllowlist(
      draft.subjectTemplate || '',
      bodyForValidate,
      allow
    );
    if (!check.ok) {
      setStatusMessage(
        `Unknown merge tokens (add to allowed list or remove from template): ${check.invalidTokens.join(', ')}`
      );
      return;
    }
    const uid = auth.currentUser?.uid || null;
    const ref = doc(firestore, TEMPLATES_COLLECTION, id);
    const htmlBody = (draft.bodyHtmlTemplate || '').trim();
    const payload = {
      key: draft.key || id,
      label: draft.label || id,
      allowedContexts: Array.isArray(draft.allowedContexts)
        ? draft.allowedContexts
        : [],
      allowedMergeFields: allow,
      category: (draft.category || '').trim(),
      isActive: draft.isActive !== false,
      format: 'html',
      subjectTemplate: draft.subjectTemplate || '',
      bodyHtmlTemplate: htmlBody,
      bodyTextTemplate: htmlToReadableText(htmlBody),
      updatedAt: serverTimestamp(),
      updatedByUid: uid,
    };
    try {
      setSaving(true);
      const existing = await getDoc(ref);
      if (existing.exists()) {
        await updateDoc(ref, payload);
      } else {
        payload.createdAt = serverTimestamp();
        await setDoc(ref, payload);
      }
      setStatusMessage('Template saved.');
      setTemplates((prev) => {
        const others = prev.filter((t) => t.id !== id);
        const merged = normalizeTemplateRecord(id, {
          ...payload,
          updatedAt: null,
          createdAt: undefined,
        });
        return [...others, merged].sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );
      });
      setDraft((d) =>
        d && d.id === id
          ? {
              ...d,
              key: payload.key,
              label: payload.label,
              allowedContexts: payload.allowedContexts,
              allowedMergeFields: payload.allowedMergeFields,
              category: payload.category,
              isActive: payload.isActive,
              format: payload.format,
              subjectTemplate: payload.subjectTemplate,
              bodyHtmlTemplate: payload.bodyHtmlTemplate,
              bodyTextTemplate: payload.bodyTextTemplate,
            }
          : d
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save email template:', err);
      setStatusMessage(err.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (selectedTemplate) {
      setDraft(unifyTemplateForAdminDraft(selectedTemplate));
      setStatusMessage('');
    }
  };

  const handleCreateNew = () => {
    setIsCreatingNew(true);
    setSelectedId('');
    setDraft({
      id: '',
      key: '',
      label: '',
      allowedContexts: [],
      allowedMergeFields: [],
      category: '',
      isActive: true,
      format: 'html',
      subjectTemplate: '',
      bodyHtmlTemplate: '',
      bodyTextTemplate: '',
      updatedAt: null,
      updatedByUid: null,
    });
    setMergeFieldInput('');
    setActiveTab('edit');
    setStatusMessage('Creating new template. Enter a key and click Save.');
  };

  const handleDuplicate = () => {
    if (!draft) return;
    setIsCreatingNew(false);
    const baseKey = `${draft.key || draft.id}_copy`;
    let key = baseKey;
    let idx = 1;
    const existingKeys = new Set(templates.map((t) => t.key));
    while (existingKeys.has(key)) {
      idx += 1;
      key = `${baseKey}_${idx}`;
    }
    const id = key;
    const copy = {
      ...draft,
      id,
      key,
      label: `${draft.label || draft.key || 'Template'} (copy)`,
      updatedAt: null,
      updatedByUid: null,
    };
    setTemplates((prev) => [...prev, copy].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })));
    setSelectedId(id);
    setDraft(copy);
    setStatusMessage('Duplicated locally — click Save to write to Firestore.');
  };

  const addMergeFieldChip = () => {
    const raw = (mergeFieldInput || '').trim();
    if (!raw) return;
    const token = raw.replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
    if (!token || !/^[a-zA-Z0-9_.]+$/.test(token)) {
      setStatusMessage('Merge field must be letters, numbers, dots, or underscores (e.g. name or client.firstName).');
      return;
    }
    const asChip = `{{${token}}}`;
    setDraft((prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev.allowedMergeFields) ? [...prev.allowedMergeFields] : [];
      if (!list.includes(asChip)) list.push(asChip);
      return { ...prev, allowedMergeFields: list };
    });
    setMergeFieldInput('');
    setStatusMessage('');
  };

  const removeMergeFieldChip = (chip) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const list = (Array.isArray(prev.allowedMergeFields) ? prev.allowedMergeFields : []).filter((c) => c !== chip);
      return { ...prev, allowedMergeFields: list };
    });
  };

  const openTestSend = () => {
    if (!draft) return;
    (async () => {
      try {
        const raw = (testTo || '').split(',').map((e) => e.trim()).filter(Boolean)[0];
        if (!raw) {
          setTestContextExtra(null);
          setShowComposer(true);
          return;
        }
        const emailLower = raw.toLowerCase();
        const flyerSnap = await getDocs(
          query(
            collection(firestore, 'flyers'),
            where('email', '==', raw)
          )
        );
        let flyer = null;
        let event = null;
        if (!flyerSnap.empty) {
          const d = flyerSnap.docs[0];
          flyer = { id: d.id, ...d.data() };
          if (flyer.eventId) {
            const evSnap = await getDoc(doc(firestore, 'events', flyer.eventId));
            if (evSnap.exists()) {
              event = { id: evSnap.id, ...evSnap.data() };
            }
          }
        } else {
          const allSnap = await getDocs(collection(firestore, 'flyers'));
          const match = allSnap.docs.find((docSnap) => {
            const data = docSnap.data() || {};
            const e = (data.email || '').toString().trim().toLowerCase();
            return e === emailLower;
          });
          if (match) {
            const data = match.data() || {};
            flyer = { id: match.id, ...data };
            if (flyer.eventId) {
              const evSnap = await getDoc(doc(firestore, 'events', flyer.eventId));
              if (evSnap.exists()) {
                event = { id: evSnap.id, ...evSnap.data() };
              }
            }
          }
        }
        setTestContextExtra(flyer || event ? { flyer, event } : null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to resolve sample flyer for test send:', err);
        setTestContextExtra(null);
      } finally {
        setShowComposer(true);
      }
    })();
  };

  const closeTestSend = () => {
    setShowComposer(false);
    setTestContextExtra(null);
  };

  const renderHistory = () => {
    if (!selectedTemplate) return null;
    return (
      <section className="templates-history">
        <h3>Recent sends for this template</h3>
        {loadingHistory ? (
          <p>Loading history…</p>
        ) : !history.length ? (
          <p>No sends recorded yet.</p>
        ) : (
          <table className="templates-history-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Context</th>
                <th>Event</th>
                <th>Sub-event</th>
                <th>Recipients</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => {
                const createdAt =
                  row.createdAt && row.createdAt.toDate
                    ? row.createdAt.toDate().toLocaleString()
                    : '';
                const recipients = historyRecipients[row.id] || [];
                const names = recipients
                  .map((r) => (r.name && r.name !== r.email ? `${r.name} <${r.email}>` : r.email))
                  .join(', ');
                return (
                  <tr key={row.id}>
                    <td>{createdAt}</td>
                    <td>{row.contextType || ''}</td>
                    <td>{row.eventId || ''}</td>
                    <td>{row.subEventLabel || ''}</td>
                    <td>
                      {recipients.length
                        ? `${recipients.length}: ${names}`
                        : row.recipientCount != null
                        ? row.recipientCount
                        : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    );
  };

  const renderFields = () => {
    const q = (fieldsSearch || '').trim().toLowerCase();
    const rows = TEMPLATE_FIELDS.filter((f) => {
      if (fieldsScope !== 'all' && f.scope !== fieldsScope) return false;
      if (!q) return true;
      const hay = `${f.token} ${f.label} ${f.description} ${(f.contexts || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
    return (
      <section className="templates-history">
        <h3>Template fields (placeholders)</h3>
        <div className="templates-fields-controls">
          <label>
            Type:{' '}
            <select value={fieldsScope} onChange={(e) => setFieldsScope(e.target.value)}>
              <option value="all">All</option>
              <option value="mailMerge">Mail-merge</option>
              <option value="legacyHtmlToken">HTML tokens</option>
              <option value="furyLab">Fury Lab</option>
            </select>
          </label>
          <input
            type="text"
            placeholder="Search token or meaning…"
            value={fieldsSearch}
            onChange={(e) => setFieldsSearch(e.target.value)}
          />
        </div>
        <table className="templates-history-table">
          <thead>
            <tr>
              <th>Token</th>
              <th>Meaning</th>
              <th>Notes</th>
              <th>Used in</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={`${f.scope}:${f.token}`}>
                <td><code>{f.token}</code></td>
                <td>{f.label}</td>
                <td>{f.description}</td>
                <td>{(f.contexts || []).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="templates-editor-empty">No matching fields.</p>}
      </section>
    );
  };

  const renderAllEmails = () => (
    <section className="templates-history">
      <h3>All emails sent (system-wide)</h3>
      {loadingAllEmails ? (
        <p>Loading…</p>
      ) : !allEmails.length ? (
        <p>No emails found.</p>
      ) : (
        <table className="templates-history-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Subject</th>
              <th>Recipients</th>
            </tr>
          </thead>
          <tbody>
            {allEmails.map((row) => {
              const createdAt =
                row.createdAt && row.createdAt.toDate
                  ? row.createdAt.toDate().toLocaleString()
                  : '';
              const to = (row.finalTo || '').toString().trim();
              const bcc = (row.finalBcc || '').toString().trim();
              const recipients = [to && `To: ${to}`, bcc && `BCC: ${bcc}`].filter(Boolean).join(' · ');
              return (
                <tr key={row.id}>
                  <td>{createdAt}</td>
                  <td>{row.subject || ''}</td>
                  <td>{recipients}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );

  const renderEditor = () => {
    if (!draft) {
      return (
        <div className="templates-editor-empty">
          {loadingTemplates ? 'Loading templates…' : 'Select a template to edit, or create a new one.'}
        </div>
      );
    }
    return (
      <section className="templates-editor">
        <header className="templates-editor-header">
          <div>
            <h2>{draft.label || draft.key}</h2>
            <p className="templates-editor-subtitle">
              Key: <code>{draft.key}</code>
              {!draft.isActive && (
                <span className="templates-editor-inactive-badge"> Inactive</span>
              )}
            </p>
          </div>
        </header>
        <div className="templates-editor-fields">
          <div className="form-group">
            <label>
              Label
              <input
                type="text"
                value={draft.label}
                onChange={(e) => handleFieldChange('label', e.target.value)}
              />
            </label>
          </div>
          <div className="form-group">
            <label>
              Key (document id; change only before first save)
              <input
                type="text"
                value={draft.key}
                onChange={(e) => handleFieldChange('key', e.target.value)}
              />
            </label>
          </div>
          <div className="form-group">
            <label>
              Category (optional tag for grouping)
              <input
                type="text"
                value={draft.category || ''}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                placeholder="e.g. newsletter, registration"
              />
            </label>
          </div>
          <div className="form-group templates-editor-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={draft.isActive !== false}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
              />
              Active (inactive templates stay in this list but can be hidden from picks later)
            </label>
          </div>
          <div className="form-group">
            <label>
              Subject template
              <input
                type="text"
                value={draft.subjectTemplate}
                onChange={(e) =>
                  handleFieldChange('subjectTemplate', e.target.value)
                }
              />
            </label>
          </div>
          <div className="form-group">
            <span className="templates-editor-label-block">Body template</span>
            <p className="templates-editor-hint">
              Edited as HTML; a plain-text version is derived automatically when you save (for clients that prefer text).
            </p>
            <EmailBodyEditor
              value={draft.bodyHtmlTemplate || ''}
              onChange={(html) => handleFieldChange('bodyHtmlTemplate', html)}
              placeholder="<p>Hello {{name}}!</p>…"
            />
          </div>
          <div className="form-group">
            <span className="templates-editor-label-block">Allowed merge fields (whitelist)</span>
            <p className="templates-editor-hint">
              If empty, any {'{{token}}'} is allowed when saving. If set, subject + body may only use these tokens.
            </p>
            <div className="templates-merge-chips">
              {(draft.allowedMergeFields || []).map((chip) => (
                <button
                  key={chip}
                  type="button"
                  className="templates-merge-chip"
                  onClick={() => removeMergeFieldChip(chip)}
                  title="Remove"
                >
                  {chip} <span aria-hidden>×</span>
                </button>
              ))}
            </div>
            <div className="templates-merge-add">
              <input
                type="text"
                value={mergeFieldInput}
                onChange={(e) => setMergeFieldInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addMergeFieldChip();
                  }
                }}
                placeholder="e.g. name or {{eventTitle}}"
              />
              <button
                type="button"
                className="templates-btn templates-btn--primary"
                onClick={addMergeFieldChip}
              >
                Add field
              </button>
            </div>
          </div>
          <div className="templates-placeholder-box">
            <div className="templates-placeholder-title">Common tokens (click to add to whitelist)</div>
            <div className="templates-placeholder-list">
              {TEMPLATE_FIELDS.filter((f) => f.scope === 'mailMerge').map((f) => (
                <button
                  key={f.token}
                  type="button"
                  className="templates-token-insert"
                  onClick={() => {
                    setDraft((prev) => {
                      if (!prev) return prev;
                      const list = Array.isArray(prev.allowedMergeFields) ? [...prev.allowedMergeFields] : [];
                      if (!list.includes(f.token)) list.push(f.token);
                      return { ...prev, allowedMergeFields: list };
                    });
                  }}
                >
                  {f.token}
                </button>
              ))}
            </div>
            <div className="templates-placeholder-hint">
              See the Fields tab for legacy HTML tokens (TEAM_MEMBERS_HTML, etc.).
            </div>
          </div>
        </div>
        <div className="templates-editor-actions">
          <button
            type="button"
            className="templates-btn templates-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save template'}
          </button>
          <button
            type="button"
            className="templates-btn templates-btn--neutral-outline"
            onClick={handleRevert}
            disabled={saving}
          >
            Revert
          </button>
          <button
            type="button"
            className="templates-btn templates-btn--neutral-outline"
            onClick={handleDuplicate}
            disabled={saving || !draft}
          >
            Duplicate
          </button>
          <div className="templates-test-send">
            <input
              type="text"
              placeholder="Test inbox address"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <button
              type="button"
              className="templates-btn templates-btn--test-outline"
              onClick={openTestSend}
              disabled={!testTo.trim()}
            >
              Send test…
            </button>
          </div>
        </div>
      </section>
    );
  };

  const renderSidebar = () => (
    <aside className="templates-sidebar">
      <div className="templates-sidebar-header">
        <h2>Templates</h2>
        <div className="templates-sidebar-header-buttons">
          <button type="button" className="templates-btn-new" onClick={handleCreateNew}>
            + New
          </button>
          <button
            type="button"
            className="templates-btn templates-btn--neutral-outline"
            onClick={handleDuplicate}
            disabled={!selectedTemplate}
          >
            Duplicate
          </button>
        </div>
      </div>
      {loadingTemplates ? (
        <p>Loading templates…</p>
      ) : !templates.length ? (
        <p>No templates found. Click “New” to create one.</p>
      ) : (
        <ul className="templates-list">
          {templates.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                className={
                  t.id === selectedId
                    ? 'templates-list-item templates-list-item--active'
                    : 'templates-list-item'
                }
                onClick={() => handleSelect(t.id)}
              >
                <span className="templates-list-label">
                  {t.label}
                  {t.isActive === false && (
                    <span className="templates-list-inactive"> · inactive</span>
                  )}
                </span>
                <span className="templates-list-key">{t.key}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );

  const initialRecipients = useMemo(() => {
    const to = (testTo || '')
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e);
    return {
      to: to.join(','),
      bcc: '',
    };
  }, [testTo]);

  const composerInitialSubject = draft ? draft.subjectTemplate : '';
  const composerInitialBody = draft
    ? (draft.bodyHtmlTemplate || '').trim()
      ? draft.bodyHtmlTemplate
      : draft.bodyTextTemplate || ''
    : '';
  const composerInitialTemplate =
    draft && COMPOSER_TEMPLATE_KEYS.has(draft.key) ? draft.key : 'custom';

  return (
    <div className="email-templates-admin">
      {renderSidebar()}
      <div className="templates-main">
        {statusMessage && (
          <p className="templates-status">
            {statusMessage}
          </p>
        )}
        <div className="templates-tabs">
          <button
            type="button"
            className={activeTab === 'edit' ? 'templates-tab templates-tab--active' : 'templates-tab'}
            onClick={() => setActiveTab('edit')}
          >
            Edit
          </button>
          <button
            type="button"
            className={activeTab === 'history' ? 'templates-tab templates-tab--active' : 'templates-tab'}
            onClick={() => setActiveTab('history')}
          >
            Emails sent
          </button>
          <button
            type="button"
            className={activeTab === 'allEmails' ? 'templates-tab templates-tab--active' : 'templates-tab'}
            onClick={() => setActiveTab('allEmails')}
          >
            All emails
          </button>
          <button
            type="button"
            className={activeTab === 'fields' ? 'templates-tab templates-tab--active' : 'templates-tab'}
            onClick={() => setActiveTab('fields')}
          >
            Fields
          </button>
        </div>

        {activeTab === 'edit' && renderEditor()}
        {activeTab === 'history' && renderHistory()}
        {activeTab === 'allEmails' && renderAllEmails()}
        {activeTab === 'fields' && renderFields()}
      </div>
      {showComposer && draft && (
        <EmailComposerModal
          isOpen={showComposer}
          onClose={closeTestSend}
          context={{ type: 'templateTest', templateKey: draft.key, ...(testContextExtra || {}) }}
          initialRecipients={initialRecipients}
          initialTemplate={composerInitialTemplate}
          initialSubject={composerInitialSubject}
          initialBody={composerInitialBody}
        />
      )}
    </div>
  );
};

export default EmailTemplatesAdmin;

